import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ApolloError } from '@apollo/client';
import { useLazyQuery } from '@apollo/client';

import type { UpdateChatHistory } from 'bundles/ai-coach-platform/components/patterns/chat/store/actions/types';
import {
  prependChatHistory,
  updateChatHistory,
} from 'bundles/ai-coach-platform/components/patterns/chat/store/dispatcher';
import type {
  AiCoachPlatform_ChatMessagesPaginatedResultQueryVariables as AiCoachChatMessagesPaginatedResultQueryVariables,
  AiCoachPlatform_ChatMessagesPaginatedResultQuery as ChatMessagesPaginatedResultQuery,
} from 'bundles/ai-coach-platform/queries/__generated__/aiCoachChatMessagesByChatSessionId';
import aiCoachChatMessagesByChatSessionIdQuery from 'bundles/ai-coach-platform/queries/aiCoachChatMessagesByChatSessionId.graphql';
import { getErrorFromApolloError } from 'bundles/ai-coach-platform/utils/errors';
import { captureMessageInSentry } from 'bundles/ai-coach-platform/utils/sentry';

import { MESSAGE_SENDER } from './constants';

type Props = {
  historyLimit: number;
  chatSessionId?: string;
  mode?: string;
};

/**
 * Parse the fetched result and update the chat history
 * @param data the data fetched from the query
 * @param prepend whether to prepend to existing messages or replace them
 */
const parseFetchedResult = (
  data: ChatMessagesPaginatedResultQuery | undefined,
  prepend: boolean = false
): UpdateChatHistory['messages'] => {
  const messages = data?.AiCoach?.queryByChatSessionId?.elements;
  if (messages) {
    const parsedMessages: UpdateChatHistory['messages'] = messages.map((message) => {
      if (message.senderType === MESSAGE_SENDER.CLIENT) {
        return {
          id: message.id,
          sender: message.senderType,
          text: message.message,
          timestamp: new Date(message.createdAt).valueOf(),
          courseraContext: message.courseraContext,
        };
      } else if (message.senderType === MESSAGE_SENDER.RESPONSE) {
        return {
          id: message.id,
          sender: message.senderType,
          text: message.message,
          timestamp: new Date(message.createdAt).valueOf(),
          /** Feedback from the user on the AI coach response via a like/dislike */
          userFeedback: message.userFeedback,
          navigationButtons: message.navigationButtonsV2,
          recommendedActions: message.recommendedActionsV2,
          courseraContext: message.courseraContext,
          recommendedEdits: message.recommendedEdits,
        };
      }
      return {
        id: message.id,
        sender: message.senderType,
        text: message.message,
        timestamp: new Date(message.createdAt).valueOf(),
        courseraContext: message.courseraContext,
        recommendedEdits: message.recommendedEdits,
      };
    });

    // TODO: MAGIC: chat history is coming in reverse order, so we make it the new messages are at the bottom of the chat by reversing it here.
    const reversedMessages = parsedMessages.reverse();

    // When a user requests to fetch earlier messages, we prepend the earlier messages to the existing chat history
    // Otherwise, we update chat history with the current messages that are fetched
    if (prepend) {
      prependChatHistory(reversedMessages);
    } else {
      updateChatHistory(reversedMessages);
    }

    return reversedMessages;
  }
  return [];
};

export function useChatHistory({ historyLimit, chatSessionId, mode }: Props): {
  loading: boolean;
  error: ApolloError | undefined;
  fetchChatHistory: (
    chatSessionId: string | undefined,
    skipLoading?: boolean
  ) => Promise<UpdateChatHistory['messages']>;
  fetchMoreChatHistory: () => Promise<void>;
  earlierMessagesAvailable: boolean;
  cursor: string | null;
} {
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<ApolloError | undefined>(undefined);

  if (chatSessionId === '') {
    captureMessageInSentry({
      error: new Error('Found empty string chat session id'),
      source: 'useChatHistory',
      level: 'info',
    });
  }

  const [fetchQuery, { loading, refetch, error: lazyQueryError }] = useLazyQuery<
    ChatMessagesPaginatedResultQuery,
    AiCoachChatMessagesPaginatedResultQueryVariables
  >(aiCoachChatMessagesByChatSessionIdQuery, {
    variables: {
      // useLazyQuery cannot be skipped, so assume chatSessionId exists at this point
      // and we handle this below in `fetchChatHistory` when it is actually called
      chatSessionId: chatSessionId!,
      limit: historyLimit,
      cursor: null,
    },
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'network-only',
  });
  const [isLoading, setIsLoading] = useState(true);
  // skip loading UI when fetching chat history e.g. from visibility change
  const [shouldSkipLoading, setShouldSkipLoading] = useState(false);

  useEffect(() => {
    if (lazyQueryError) {
      setError(lazyQueryError);
      setIsLoading(false);
      setShouldSkipLoading(false);

      const apolloError = getErrorFromApolloError(lazyQueryError);

      if (apolloError) {
        captureMessageInSentry({ error: apolloError, coachMode: mode, source: 'useChatHistory' });
      }
    } else {
      setIsLoading(shouldSkipLoading ? false : loading);
      setError(undefined);
    }
  }, [lazyQueryError, loading, shouldSkipLoading, mode]);

  const fetchChatHistory = useCallback<
    (chatSessionIdToFetch: string | undefined, skipLoading?: boolean) => Promise<UpdateChatHistory['messages']>
  >(
    async (chatSessionIdToFetch: string | undefined, skipLoading = false) => {
      if (!chatSessionIdToFetch) {
        setIsLoading(false);
        return [];
      }
      setShouldSkipLoading(skipLoading);

      if (!skipLoading) {
        setIsLoading(true);
      } else {
        setIsLoading(false);
      }

      const { data, error: fetchQueryError } = await fetchQuery({
        variables: { chatSessionId: chatSessionIdToFetch, limit: historyLimit },
      });

      if (fetchQueryError) {
        setError(fetchQueryError);
        setIsLoading(false);

        const fetchError = getErrorFromApolloError(fetchQueryError);

        if (fetchError) {
          captureMessageInSentry({ error: fetchError, coachMode: mode, source: 'fetchChatHistory' });
        }
        return [];
      }

      // Update cursor to the next cursor
      setCursor(data?.AiCoach?.queryByChatSessionId?.pagination?.cursor || null);
      const result = parseFetchedResult(data, false);

      if (!skipLoading) {
        // set loading after parsing so the updated UI is accurate, see [COACH-1210] for more.
        setIsLoading(false);
      }
      return result;
    },
    [fetchQuery, historyLimit, mode]
  );

  return useMemo(
    () => ({
      loading: isLoading,
      error,
      fetchChatHistory,
      fetchMoreChatHistory: async () => {
        if (cursor) {
          const { data, error: fetchMoreError } = await refetch({ cursor, limit: historyLimit });
          setCursor(data?.AiCoach?.queryByChatSessionId?.pagination?.cursor || null);

          if (fetchMoreError) {
            setError(fetchMoreError);

            const fetchError = getErrorFromApolloError(fetchMoreError);
            if (fetchError) {
              captureMessageInSentry({ error: fetchError, coachMode: mode, source: 'fetchMoreChatHistory' });
            }
          } else {
            // Parse and prepend new messages
            parseFetchedResult(data, true);
          }
        }
      },
      earlierMessagesAvailable: cursor !== null,
      cursor,
    }),
    [isLoading, error, fetchChatHistory, historyLimit, refetch, cursor, mode]
  );
}
